import type { HTMLAttributes } from 'react'

import { type VariantProps, cva } from 'class-variance-authority'

import { cn } from '@/lib/ui/cn'

const pillVariants = cva(
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[length:calc(var(--text-xs)*0.92)] font-semibold leading-5 tracking-[0.04em]',
  {
    variants: {
      tone: {
        neutral: 'border-[color:var(--bl-line)] bg-[color:var(--bl-sunken)] text-[color:var(--bl-ink-2)]',
        gold: 'border-[rgb(var(--color-primary)/0.35)] bg-[color:var(--bl-gold-wash)] text-[color:var(--bl-gold-text)]',
        success: 'border-[rgb(var(--color-success)/0.35)] bg-[rgb(var(--color-success)/0.1)] text-[rgb(var(--color-text-success))]',
        warning: 'border-[rgb(var(--color-warning)/0.35)] bg-[rgb(var(--color-warning)/0.1)] text-[rgb(var(--color-text-warning))]',
        error: 'border-[rgb(var(--color-error)/0.35)] bg-[rgb(var(--color-error)/0.1)] text-[rgb(var(--color-text-error))]',
        info: 'border-[color:var(--bl-line-strong)] bg-[rgb(var(--color-info)/0.1)] text-[rgb(var(--color-text-info))]',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
)

export type PillTone = NonNullable<PillProps['tone']>

const DOT: Record<PillTone, string> = {
  neutral: 'bg-[color:var(--bl-ink-3)]',
  gold: 'bg-[rgb(var(--color-primary))] shadow-[0_0_8px_var(--bl-glow)]',
  success: 'bg-[rgb(var(--color-success))] shadow-[0_0_8px_rgb(var(--color-success)/0.7)]',
  warning: 'bg-[rgb(var(--color-warning))]',
  error: 'bg-[rgb(var(--color-error))]',
  info: 'bg-[rgb(var(--color-info))]',
}

export interface PillProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof pillVariants> {
  /** A small lit dot before the label. */
  dot?: boolean
}

/** A status or category label. */
export function Pill({ tone, dot = false, className, children, ...props }: PillProps) {
  return (
    <span className={cn(pillVariants({ tone }), className)} {...props}>
      {dot ? <span aria-hidden="true" className={cn('h-1.5 w-1.5 rounded-full', DOT[tone ?? 'neutral'])} /> : null}
      {children}
    </span>
  )
}
